import { ControlSheetRow } from '@/modules/ControlSheet/types/types'
import styles from './ControlSheetTable.module.scss'
import { Table } from '@tanstack/react-table'
import { TableHeader } from '@/ui/Table'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { tripContainsTime } from '@/modules/ControlSheet/helpers/tripContainsTime'
import { rowId } from '@/modules/ControlSheet/helpers/rowId'

interface Props {
    table: Table<ControlSheetRow>
    scrollToTimeRow: string | null
    scrollToTripRow: string | null
}

type RowStatus = 'first' | 'mid' | 'last' | 'only'

const getRowStatus = (isFirst: boolean, isLast: boolean): RowStatus => {
    if (isFirst) {
        if (isLast) {
            return 'only'
        } else {
            return 'first'
        }
    } else {
        if (isLast) {
            return 'last'
        } else {
            return 'mid'
        }
    }
}

export const ControlSheetTable = ({ table, scrollToTimeRow, scrollToTripRow }: Props) => {
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})

    useEffect(() => {
        if (scrollToTimeRow && table.getRowModel().rows.length > 0) {
            const rowToScrollTo = table.getRowModel().rows.find((row) => {
                return row.original.outbound && tripContainsTime(row.original.outbound, scrollToTimeRow)
            })

            if (rowToScrollTo?.id) {
                const rowElement = rowRefs.current[rowId(rowToScrollTo.original)]
                if (rowElement) {
                    rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }
        }
    }, [scrollToTimeRow, table])

    useEffect(() => {
        if (scrollToTripRow && table.getRowModel().rows.length > 0) {
            const rowToScrollTo = table.getRowModel().rows.find((row) => {
                return row.original.outbound?.trip === scrollToTripRow || row.original.inbound?.trip === scrollToTripRow
            })

            if (rowToScrollTo?.id) {
                const rowElement = rowRefs.current[rowToScrollTo.id]
                if (rowElement) {
                    rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            }
        }
    }, [scrollToTimeRow, scrollToTripRow, table])

    const rows = table.getFilteredRowModel().rows
    let currentRun: string = ''

    const flatMapped = rows.flatMap((row, index) => {
        const controlSheetRow = row.original

        const isFirstInRun = controlSheetRow.run !== currentRun
        currentRun = controlSheetRow.run
        const isLastInRun = index === rows.length - 1 || rows[index + 1].original.run !== currentRun

        const rowStatus = getRowStatus(isFirstInRun, isLastInRun)

        return [
            <tr
                key={row.id}
                className={clsx(styles.row, {
                    [styles.rowFirst]: rowStatus === 'first' || rowStatus === 'only',
                    [styles.rowMid]: rowStatus === 'mid',
                    [styles.rowLast]: rowStatus === 'last' || rowStatus === 'only',
                    [styles.rowOnly]: rowStatus === 'only',
                })}
                ref={(el) => (rowRefs.current[row.id] = el)}
            >
                {(() => {
                    return row
                        .getVisibleCells()
                        .map((cell) => {
                            if (cell.getValue() === 'SKIP_CELL') return null

                            const colSpan = cell.column.columnDef.meta?.getColSpan?.(row.original) || 1

                            return (
                                <td key={cell.id} className={clsx(styles.cell)} colSpan={colSpan}>
                                    {typeof cell.column.columnDef.cell === 'function'
                                        ? cell.column.columnDef.cell(cell.getContext())
                                        : cell.getValue()}
                                </td>
                            )
                        })
                        .filter((it) => it !== null)
                })()}
            </tr>,
            ...(rowStatus === 'last' || rowStatus === 'only'
                ? [<tr key={`${row.id}-separator`} className={styles.rowSeparator} />]
                : []),
        ]
    })

    return (
        <table className={styles.table}>
            <TableHeader table={table} />
            <tbody>{flatMapped}</tbody>
        </table>
    )
}
